import moment from 'moment'
import { useLocation, useHistory } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRangePickerValue } from '@tremor/react'
import { DateFilter, dateFormat } from '../types/date-filter'

export default function useDateFilter() {
  const location = useLocation()
  const history = useHistory()
  const [dateRangePickerValue, setDateRangePickerValue] =
    useState<DateRangePickerValue>()

  const setDateFilter = useCallback(
    ([startDate, endDate, value]: DateRangePickerValue) => {
      const lastDays = value ?? DateFilter.Custom

      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('last_days', lastDays)

      if (lastDays === DateFilter.Custom && startDate && endDate) {
        searchParams.set('start_date', moment(startDate).format(dateFormat))
        searchParams.set('end_date', moment(endDate).format(dateFormat))
      } else {
        searchParams.delete('start_date')
        searchParams.delete('end_date')
      }
      history.push({
        pathname: location.pathname,
        search: searchParams.toString()
      }, { scroll: false })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const searchParams = new URLSearchParams(location.search)
  const lastDaysParam = searchParams.get('last_days') as DateFilter
  const lastDays: DateFilter =
    typeof lastDaysParam === 'string' &&
    Object.values(DateFilter).includes(lastDaysParam)
      ? lastDaysParam
      : DateFilter.Last7Days

  const { startDate, endDate } = useMemo(() => {
    const today = moment().utc()
    if (lastDays === DateFilter.Custom) {
      const startDateParam = searchParams.get('start_date') as string
      const endDateParam = searchParams.get('end_date') as string

      const startDate =
        startDateParam ||
        moment(today)
          .subtract(+DateFilter.Last7Days, 'days')
          .format(dateFormat)
      const endDate = endDateParam || moment(today).format(dateFormat)

      return { startDate, endDate }
    }

    const startDate = moment(today)
      .subtract(+lastDays, 'days')
      .format(dateFormat)
    const endDate =
      lastDays === DateFilter.Yesterday
        ? moment(today)
            .subtract(+DateFilter.Yesterday, 'days')
            .format(dateFormat)
        : moment(today).format(dateFormat)

    return { startDate, endDate }
  }, [lastDays, searchParams])

  useEffect(() => {
    setDateRangePickerValue([
      moment(startDate).toDate(),
      moment(endDate).toDate(),
      lastDays === DateFilter.Custom ? null : lastDays,
    ])
  }, [startDate, endDate, lastDays])

  const onDateRangePickerValueChange = useCallback(
    ([startDate, endDate, value]: DateRangePickerValue) => {
      if (startDate && endDate) {
        setDateFilter([startDate, endDate, value])
      } else {
        setDateRangePickerValue([startDate, endDate, value])
      }
    },
    [setDateFilter]
  )

  return {
    startDate,
    endDate,
    dateRangePickerValue,
    onDateRangePickerValueChange,
  }
}
